import React from 'react';
import { getStaticContent } from '../constants/staticContent';
import productImage from '../assets/product1.jpg';

const HomeSection = ({ language = 'en' }) => {
  const { HERO_CONTENT, FEATURES_CONTENT } = getStaticContent(language);
  const highlightFeatures = FEATURES_CONTENT.features;

  return (
    <section id="home" className="min-h-screen flex flex-col">
      {/* Hero Section - Original Design — DO NOT TOUCH */}
      <div className="flex-1 relative w-full px-4 sm:px-6 md:px-10 py-6 sm:py-9 bg-white overflow-hidden">
        
        {/* Animations */}
        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(24px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeInLeft {
            from { opacity: 0; transform: translateX(-24px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes fadeInScale {
            from { opacity: 0; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1); }
          }
          .fade-left { animation: fadeInLeft 0.8s ease-out both; }
          .fade-up-1 { animation: fadeInUp 0.8s ease-out 0.15s both; }
          .fade-up-2 { animation: fadeInUp 0.8s ease-out 0.3s both; }
          .fade-up-3 { animation: fadeInUp 0.8s ease-out 0.45s both; }
          .fade-scale { animation: fadeInScale 0.6s ease-out both; }

          /* Feature card premium hover */
          .feature-card {
            background: #ffffff;
            border: 1px solid rgba(0,0,0,0.06);
            border-radius: 16px;
            box-shadow: 0 2px 12px rgba(0,0,0,0.07);
            transition: all 0.25s ease;
          }
          .feature-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 28px rgba(0,0,0,0.12);
          }
        `}</style>

        {/* Hero Card */}
        <div
          className="relative w-full max-w-9xl mx-auto rounded-3xl overflow-hidden shadow-2xl
                     h-[420px] sm:h-[480px] md:h-[540px] lg:h-[600px] xl:h-[660px]"
          style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)' }}
        >
          {/* Background Image */}
          <img
            src={productImage}
            alt="AI-powered smart farming technology"
            className="absolute inset-0 w-full h-full object-cover object-center
                       transition-transform duration-700 ease-out
                       hover:scale-[1.04] will-change-transform"
          />

          {/* Gradient Overlay */}
          <div
            className="absolute inset-0 z-20"
            style={{
              background: `
                linear-gradient(to right,
                  rgba(28, 55, 20, 0.8) 0%,
                  rgba(45, 80, 22, 0.65) 32%,
                  rgba(0, 0, 0, 0.3) 52%,
                  rgba(0, 0, 0, 0.1) 70%,
                  rgba(0, 0, 0, 0) 100%
                )
              `
            }}
          />

          {/* Content */}
          <div className="relative z-30 h-full flex items-center">
            <div className="w-full md:w-[52%] px-4 sm:px-6 md:px-14 text-left text-white">

              {/* Label */}
              <div
                className="fade-left inline-flex items-center gap-2 px-3 sm:px-5 py-2 mb-4 sm:mb-6
                           rounded-full text-xs font-semibold tracking-widest uppercase
                           text-emerald-100"
                style={{
                  background: 'rgba(255,255,255,0.16)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                 {HERO_CONTENT.label}
              </div>

              {/* Heading */}
              <h1
                className="fade-up-1 mb-4 sm:mb-6 max-w-xl
                           text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-white"
                style={{ 
                  textShadow: '0 4px 24px rgba(0,0,0,0.6)',
                  letterSpacing: '-0.02em'
                }}
              >
                {HERO_CONTENT.heading}
              </h1>

              {/* Description */}
              <p
                className="fade-up-2 max-w-lg
                           text-base md:text-lg font-light leading-relaxed text-emerald-50"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}
              >
                {HERO_CONTENT.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="bg-gray-50 border-t border-gray-100 py-8 sm:py-12 md:py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Eyebrow label */}
          <div className="text-center mb-8">
            <span
              className="inline-block text-xs font-bold tracking-widest uppercase text-emerald-700 bg-emerald-50 px-4 py-1.5 rounded-full border border-emerald-200"
            >
              {FEATURES_CONTENT.eyebrow}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {highlightFeatures.map((feature, index) => (
              <div
                key={index}
                className="fade-scale feature-card text-center p-6 sm:p-8"
                style={{ animationDelay: `${0.6 + index * 0.1}s` }}
              >
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 bg-emerald-600 rounded-2xl flex items-center justify-center text-white shadow-md">
                  <feature.icon className="w-6 h-6 md:w-8 md:h-8" />
                </div>
                <h3
                  className="text-xl md:text-2xl font-bold text-gray-900 mb-2"
                >
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeSection;
